import { push } from 'connected-react-router';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { fetchAccessTokenIfNeeded } from '~/AccessToken';
import { UserProductGroup } from '~/Api/User/ProductGroup';
import { fetchCurrentCustomerDatasourceLicencesIfNeeded } from '~/CustomerDatasourceLicence';
import { formatCondensedHumanDate } from '~/DateTime';
import { handleError } from '~/ErrorHandler';
import { flashSuccessMessage } from '~/FlashMessage';
import { Product } from '~/Product';
import { fetchGroup, pruneGroups, selectGroup, selectUpdate } from '~/ProductGroup';
import { updatePriceTypePreference } from '~/ProductPrice';
import buildProductGroupUrl from '~/Router/buildProductGroupUrl';
import { State, ThunkDispatch } from '~/Store';

import DetailPage from './DetailPage';

function mapStateToProps(
	state: State,
	props: {
		groupId: string | null;
		productId: string | null;
		productTabName?: string;
		location: { search: string };
	}
): {
	groupId: string | null;
	group: UserProductGroup | null;
	groupNotFound: boolean;
	isFetchingGroup: boolean;
	isUpdatingNotification: boolean;
	productId: string | null;
	productTabName?: string;
	formatDate: (date: Date, includeYear?: boolean, locale?: string[]) => string;
} {
	const { productGroup } = state;
	const { groupId, productId, productTabName } = props;
	const groupState = selectGroup(productGroup, groupId);

	return {
		groupId,
		group: groupState && groupState.status === 'fetched' ? groupState.group : null,
		groupNotFound: Boolean(groupState && groupState.status === 'not-found'),
		isFetchingGroup: Boolean(groupState && groupState.status === 'fetching'),
		isUpdatingNotification: selectUpdate(state.productGroup, groupId) !== null,
		productId,
		productTabName,
		formatDate: formatCondensedHumanDate,
	};
}

function mapDispatchToProps(
	dispatch: ThunkDispatch<AnyAction>,
	props: {
		groupId: string | null;
		productId: string | null;
		productTabName?: string;
		location: { search: string };
	}
): {
	productDetailLink(product: Product): { pathname: string; search: string };
	onFetch(): void;
	onPrune(): void;
	onCloseDetail(): void;
	onShowDetail(productId: string, tabName: string): void;
	onRemoved(group: UserProductGroup): void;
	onQuery(a: string | null, b: string | null, c: string | null, d: boolean, e: boolean, f: boolean): void;
	onProductPriceTypeChanged(): void;
} {
	const { groupId } = props;

	return {
		productDetailLink: (product: { id: string }) => ({
			pathname: `/product-groups/${encodeURIComponent(groupId || '')}/${encodeURIComponent(product.id)}`,
			search: props.location?.search || '',
		}),
		onFetch: async () => {
			if (groupId === null) {
				return;
			}

			try {
				await dispatch(fetchAccessTokenIfNeeded());
			} catch (error) {
				dispatch(handleError(error));
			}
			await dispatch(fetchCurrentCustomerDatasourceLicencesIfNeeded());
			dispatch(fetchGroup(groupId));
		},
		onPrune: () => dispatch(pruneGroups()),
		onCloseDetail: () =>
			dispatch(
				push({
					pathname: `/product-groups/${encodeURIComponent(groupId || '')}/`,
					search: props.location?.search || '',
				})
			),
		onShowDetail: (productId: string, tabName: string) =>
			dispatch(
				push({
					pathname: `/product-groups/${encodeURIComponent(groupId || '')}/${encodeURIComponent(productId)}${
						tabName !== 'info' ? `/${encodeURIComponent(tabName)}` : ''
					}`,
					search: props.location?.search || '',
				})
			),
		onRemoved: (group: UserProductGroup) => {
			dispatch(
				push({
					pathname: `/product-groups/${group.parent !== null ? encodeURIComponent(group.parent.id) : ''}`,
				})
			);
		},
		onQuery: (
			productId: string,
			currency: string,
			priceType: string | null,
			showNotificationSettings: boolean,
			filterCalculatedPrice: boolean,
			showHistory: boolean
		) => {
			if (!groupId) {
				return;
			}

			dispatch(
				push(
					buildProductGroupUrl(
						groupId,
						priceType,
						productId,
						currency,
						showNotificationSettings,
						filterCalculatedPrice,
						showHistory
					)
				)
			);
			dispatch(updatePriceTypePreference(priceType, filterCalculatedPrice));
		},
		onProductPriceTypeChanged: () => dispatch(flashSuccessMessage('Products price type has been changed')),
	};
}

export default connect(mapStateToProps, mapDispatchToProps)(DetailPage);
